import React, { useEffect, useState } from "react";
import { useInView } from "react-intersection-observer";
import { cn } from "../../lib/utils";
import Error500Page from "../../pages/500";
import EmptyStore from "../EmptyStore";
import RestorableList from "../Home/RestorableList";
import SkeletonCard from "../SkeletonCard";
import StoreItem from "../StoreItem";

const ProductListing = ({
  store,
  wrapperClassName = "",
  useVirtualizer = false,
  storeProductsQueryProps,
  search = false,
  searchQuery = "",
  toggleDrawer = () => {},
}) => {
  const [lastEl, setLastEl] = useState(null);
  const { ref, inView } = useInView({
    threshold: 0,
  });

  const {
    data: productData,
    isLoading: isProductsLoading,
    isFetchingNextPage,
    fetchNextPage,
    hasNextPage,
    error: productError,
  } = storeProductsQueryProps;
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const products = productData?.pages?.flatMap((page) => page.products) || [];

  useEffect(() => {
    let timer;
    if (inView && hasNextPage && !isFetchingNextPage) {
      timer = setTimeout(() => {
        fetchNextPage();
      }, 100);
    }
    return () => {
      window.clearTimeout(timer);
    };
  }, [inView, hasNextPage, isFetchingNextPage, fetchNextPage]);

  useEffect(() => {
    if (!products.length) return;
    const [lastElement] = products.slice(-1);
    setLastEl(lastElement.id);
  }, [products]);

  if (productError) {
    return <Error500Page />;
  }
  return (
    <div className={cn("grid mt-4 gap-[11px_22px] relative pt-16 min-h-[80vh]", wrapperClassName)}>
      {products.length > 0 ? (
        <RestorableList useVirtualizer={useVirtualizer}>
          {React.Children.toArray(
            products.map((_, index) => {
              const startIndex = index * 2;
              const endIndex = startIndex + 2;
              const rowProducts = products.slice(startIndex, endIndex);
              if (!rowProducts.length) return null;
              return (
                <div className={cn("grid grid-cols-2 gap-4", search && "px-6")}>
                  {React.Children.toArray(
                    rowProducts.map((product) => (
                      <div ref={lastEl === product.id ? ref : null}>
                        <StoreItem
                          cohort={store.cohort.includes(2) ? store.cohort : null}
                          product={product}
                          top={index === 0}
                          toggleDrawer={toggleDrawer}
                        />
                      </div>
                    )),
                  )}
                </div>
              );
            }),
          )}
        </RestorableList>
      ) : (
        !isProductsLoading && <EmptyStore store={store} search={search} searchQuery={searchQuery} />
      )}
      {/* Loader for next page */}
      {(isFetchingNextPage || isProductsLoading) && (
        <div className={cn("grid grid-cols-2 gap-4", search && "px-6")}>
          {Array(6)
            .fill(0)
            .map((_, i) => (
              <div key={i}>
                <SkeletonCard
                  imageClass="rounded-lg object-cover max-w-[none] w-[calc((100vw/2)-33px)] h-[calc((100vw/2)-33px)] md:w-[calc((520px/2)-33px)] md:h-[calc((520px/2)-33px)]"
                  titleClass="h-4 w-[calc((100vw/2)-33px)] md:w-[calc((520px/2)-33px)]"
                  descriptionClass="h-4 w-[calc((100vw/2)-150px)] md:w-[calc((520px/2)-150px)]"
                  containerClass="flex justify-start items-start flex-col max-w-[calc((100vw/2)-33px)] mb-8 md:max-w-[calc((520px/2)-33px)]"
                  key={i}
                />
              </div>
            ))}
        </div>
      )}
    </div>
  );
};

export default ProductListing;
